// 頁型模板⑦：unresolved（6 筆 route）。
//
// Gate 1 盤點把這些候選標成 classification「未定」：光看原始碼無法判斷它們是真的頁面、
// 只是 include 片段，還是已經沒有任何入口。要等 S2-3 在原系統實跑觸達之後才能定案。
//
// 因此這個模板**不猜**畫面長什麼樣，只把待決事實攤開：pageId 與原系統 URL。
// 它沒有任何 scenario 預設集 —— 連 default 都不算，因為「這一頁是什麼」本身還沒定。
// 硬做一個假的清單或表單畫面，會讓後續稽核誤以為這幾頁已經有比對基準。
//
// 【證據等級】§7 第 3 級（盤點結果）。

import type { ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { ContentPanel } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import type { PageTemplateProps } from './types';
import { DialogHost } from './shared';
import { pageBehavior } from './page-behaviors';

export function UnresolvedPage({ route }: PageTemplateProps): ReactElement {
  const pageId = route.pageId;
  // 批次 20（R20-2）：unresolved 頁的整頁置換 hook（dashboardBody 同款 opt-in 先例，
  // CrudListPage 的早退同款）。未登錄頁照原樣（待決 notice）零影響。
  const behavior = pageBehavior(pageId);
  // DialogHost（批次 20）：置換 body（DashboardBody）呼叫 useDialogs，必須自帶 Provider
  // （CrudListPage:94 同款）；Provider 對話框關閉時不 render DOM，notice 頁零影響。
  if (behavior?.unresolvedBody !== undefined) {
    return <DialogHost route={route}>{behavior.unresolvedBody({ pageId })}</DialogHost>;
  }
  return (
    // 模板只負責內容區：全站外框（ApplicationShell／Navbar／SidebarMenu／Breadcrumb）
    // 由路由層的 AppLayout 提供，模板再包一層會變成殼中殼。
    // 這個 <div> 是整頁的根節點，data-ui-id 就是 §10.1 說的 pageId 本身。
    <div data-ui-id={pageId}>
      <ContentPanel
        uiId={`${pageId}-panel`}
        title={route.title}
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        <p data-ui-id={`${pageId}-unresolved-notice`} className="text-sm">
          {t('unresolved.notice')}
        </p>
        <dl data-ui-id={`${pageId}-unresolved-detail`} className="mt-3 flex flex-col gap-1 text-sm">
          <dt data-ui-id={`${pageId}-unresolved-page-id-label`} className="text-ink-muted">
            {t('unresolved.pageId')}
          </dt>
          <dd data-ui-id={`${pageId}-unresolved-page-id`}>{route.pageId}</dd>
          <dt data-ui-id={`${pageId}-unresolved-legacy-url-label`} className="text-ink-muted">
            {t('unresolved.legacyUrl')}
          </dt>
          <dd data-ui-id={`${pageId}-unresolved-legacy-url`}>
            {route.legacyUrl ?? t('unresolved.none')}
          </dd>
        </dl>
      </ContentPanel>
    </div>
  );
}
